import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { STAFF, VALID_KAIROO_PERMISSIONS } from "../../constants/testIds";
import { FormField } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

export function CreateKairooRoleDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();

  const createMutation = useMutation({
    mutationFn: () => api.post("/control/kairoo-roles", { name, permissions }),
    onSuccess: () => pulse(() => {
      toast.success(`${name} role created`);
      queryClient.invalidateQueries({ queryKey: ["kairoo-roles"] });
      setName("");
      setPermissions([]);
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal" data-testid={STAFF.createRoleDialog}>
        <DialogHeader><DialogTitle>Create a Kairoo role</DialogTitle></DialogHeader>
        <FormField label="Role name" required testId={STAFF.roleNameInput} value={name} onChange={setName} />
        <div className="space-y-2">
          <Label>Permissions</Label>
          {VALID_KAIROO_PERMISSIONS.map((perm) => (
            <label key={perm} className="flex items-center gap-2 text-sm">
              <Checkbox
                data-testid={STAFF.permissionCheckbox(perm)}
                checked={permissions.includes(perm)}
                onCheckedChange={(checked) => setPermissions(checked ? [...permissions, perm] : permissions.filter((p) => p !== perm))}
              />
              {perm}
            </label>
          ))}
        </div>
        <DialogFooter>
          <SubmitButton testId={STAFF.createRoleSubmit} disabled={!name} pending={createMutation.isPending} success={success} onClick={() => createMutation.mutate()} label="Create role" successLabel="Created" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
